import { useTranslation } from 'react-i18next';

import { formatDateTime, formatDurationMs } from '~/utils/date';

import type { RunProgress, RunRead } from '../types/run.api.types';
import { hasField } from '../utils/catalog-capabilities';
import { catalogLabel } from '../utils/catalog-labels';
import { stageCount, statusTone } from '../utils/run-format';

import { useRunLabels } from './use-run-labels';

/** The one thing the header offers for the run's state; `null` when nothing needs the user. */
export type RunNextAction =
  | { kind: 'experiment'; experimentId: string }
  | { kind: 'review'; tab: 'products' | 'overview' }
  | { kind: 'recovery' }
  | { kind: 'delivery' }
  | { kind: 'resume' };

/** What the header shows for the run's state: the status chip (code + text + hover) and its action. */
export interface RunNextStep {
  /** Status code that tones the chip. */
  status: string;
  label: string;
  /** Hover text: failure reason, exact waiting / pause time, what pausing means. */
  hint: string | null;
  action: RunNextAction | null;
}

/**
 * State → one status chip + ONE primary action for the run header (R1) — the job the old
 * next-step banner did. Never offers a step that only re-opens the tab on screen (Q27); an
 * experiment run's open gate is decided in the experiment (B2), so its action goes there.
 */
export function useRunNextStep(
  run: RunRead | undefined,
  progress: RunProgress | undefined,
  activeTab: string,
): RunNextStep | null {
  const { t } = useTranslation('catalog-v2-run');
  const { t: tCatalog } = useTranslation('catalog-v2');
  const { stageLabel, statusLabel } = useRunLabels();

  const runStatus = progress?.runStatus ?? run?.status ?? '';
  if (!runStatus) return null;
  if (!progress) {
    return { status: runStatus, label: statusLabel(runStatus), hint: null, action: null };
  }
  const experimentId = progress.experimentId ?? run?.experimentId ?? null;

  if (progress.pendingGate) {
    const gate = progress.pendingGate;
    const waited = formatDurationMs(gate.waitingMs);
    // Option 2 — every gate but the Publish approval is decided in the panel on Products; the
    // Publish approval is decided in Summary's top band (the Publishing tab folded into it).
    const tab = gate.stage === 'APPROVAL' ? 'overview' : 'products';
    // §49 `apiOwner` names who decides this gate; older backends omit it → every gate of an
    // experiment run is decided in the experiment.
    const experimentDecides =
      !!experimentId && (hasField(gate, 'apiOwner') ? gate.apiOwner === 'experiment' : true);
    return {
      status: 'AWAITING_GATE',
      label: waited
        ? t('nextStep.status.gateWaited', { stage: stageLabel(gate.stage), waited })
        : t('nextStep.status.gate', { stage: stageLabel(gate.stage) }),
      hint: gate.waitingSince
        ? t('nextStep.status.waitingSince', { at: formatDateTime(gate.waitingSince) })
        : null,
      // B2: an experiment run is decided in the experiment; else Products / Summary, unless
      // the user is already on it (the decision panel / bar is right there).
      action:
        experimentDecides && experimentId
          ? { kind: 'experiment', experimentId }
          : activeTab === tab
            ? null
            : { kind: 'review', tab },
    };
  }

  if (runStatus === 'FAILED' || runStatus === 'TERMINATED') {
    return {
      status: runStatus,
      label: progress.currentStage
        ? t('nextStep.failed.titleAt', { stage: stageLabel(progress.currentStage) })
        : statusLabel(runStatus),
      hint: progress.failureReason ?? t('nextStep.failed.noReason'),
      action: activeTab === 'recovery' ? null : { kind: 'recovery' },
    };
  }

  if (runStatus === 'COMPLETED') {
    const delivery = progress.delivery ?? null;
    const hasDeliveryStage = progress.stages.some(
      (s) => s.stage === 'APPROVAL' || s.stage === 'DELIVERY',
    );
    if (!hasDeliveryStage && !delivery) {
      return { status: runStatus, label: t('nextStep.completed'), hint: null, action: null };
    }
    const destination = progress.connectorCode
      ? catalogLabel(tCatalog, 'connector', progress.connectorCode)
      : t('nextStep.destinationFallback');
    const delivered =
      delivery?.delivered ??
      stageCount(
        progress.stages.find((s) => s.stage === 'DELIVERY'),
        'products_succeeded',
      );
    // "A COMPLETED run can still show a FAILED delivery" — the chip must not read green then.
    const deliveryFailed = !!delivery && statusTone(delivery.status) === 'failed';
    return {
      status: deliveryFailed ? delivery.status : runStatus,
      label: deliveryFailed
        ? t('nextStep.status.deliveryFailed')
        : typeof delivered === 'number'
          ? t('nextStep.status.delivered', { count: delivered, destination })
          : t('nextStep.completed'),
      hint: delivery?.lastError ?? null,
      // "Did it land" is Summary's Publish pane now — no action while already on Summary.
      action: activeTab === 'overview' ? null : { kind: 'delivery' },
    };
  }

  if (progress.paused) {
    return {
      status: 'PAUSED',
      label: t('header.paused'),
      hint: progress.pausedAt
        ? t('nextStep.paused.sinceAt', { at: formatDateTime(progress.pausedAt) })
        : t('nextStep.paused.since'),
      action: { kind: 'resume' },
    };
  }

  if (runStatus === 'RUNNING') {
    const stage = progress.stages.find((s) => s.stage === progress.currentStage);
    const succeeded = stageCount(stage, 'products_succeeded');
    const expected = stageCount(stage, 'products_expected');
    return {
      status: 'RUNNING',
      label: !progress.currentStage
        ? t('nextStep.runningNoStage')
        : expected
          ? t('nextStep.status.runningCount', {
              stage: stageLabel(progress.currentStage),
              done: succeeded ?? 0,
              expected,
            })
          : t('nextStep.status.running', { stage: stageLabel(progress.currentStage) }),
      hint: null,
      action: null,
    };
  }

  // PENDING (queued), RESET, SKIPPED or any other state with nothing for the user to do.
  return { status: runStatus, label: statusLabel(runStatus), hint: null, action: null };
}
